import React, { useRef, useState } from "react";
import { FileUp, Loader2, UploadCloud, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../console/api";

export const FinanceImportPanel = ({ onClose, onImported }) => {
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const readFile = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      setRaw(text);
      toast.success(`${file.name} loaded and ready to import.`);
    } catch { toast.error("Couldn't read that statement file."); }
  };

  const submit = async () => {
    if (!raw.trim()) return toast.error("Paste a statement or choose a CSV file first.");
    setBusy(true);
    try {
      const response = await api.post("/admin/financial-overview/import", { raw });
      toast.success(`Imported ${response.data.imported} transaction(s); ${response.data.skipped_duplicates} duplicate(s) skipped.`);
      setRaw("");
      await onImported();
    } catch (error) { toast.error(error?.response?.data?.detail || "Couldn't import that statement."); }
    finally { setBusy(false); }
  };

  return (
    <section className="border bg-white p-5" style={{ borderColor: "#E8DFCB" }} data-testid="finance-import-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.35em] uppercase font-semibold text-[#6E1E3A]">Import bank statement</div>
          <p className="text-xs italic text-[#7A6A55] mt-1">Upload a CSV/text file or paste rows with a date, description and amount. Positive amounts become income; negative amounts become outgoing.</p>
        </div>
        <button type="button" onClick={onClose} data-testid="finance-import-close" aria-label="Close importer" className="p-2 text-[#6E1E3A]"><X className="w-4 h-4" /></button>
      </div>
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-4">
        <button type="button" onClick={() => fileRef.current?.click()} data-testid="finance-import-file-button"
          className="min-h-36 border-2 border-dashed flex flex-col items-center justify-center gap-2 text-sm font-semibold text-[#5A4565] hover:bg-[#FBF6E9]" style={{ borderColor: "#C9A268" }}>
          <UploadCloud className="w-7 h-7" /> Choose CSV or text file
        </button>
        <input ref={fileRef} type="file" accept=".csv,.txt,text/csv,text/plain" onChange={(event) => readFile(event.target.files?.[0])} data-testid="finance-import-file-input" className="sr-only" />
        <label className="text-xs">
          <span className="block mb-1 font-semibold tracking-[0.2em] uppercase text-[10px] text-[#6E1E3A]">Statement rows</span>
          <textarea value={raw} onChange={(event) => setRaw(event.target.value)} rows={7} data-testid="finance-import-textarea"
            placeholder={'2026-08-20, HD-1042 Jane Smith, 85.00\n2026-08-21, NZ Post Courier, -18.40'}
            className="w-full min-h-36 p-3 border bg-[#FFFEFA] font-mono text-xs resize-y" style={{ borderColor: "#E8DFCB" }} />
        </label>
      </div>
      <div className="mt-4 flex justify-end">
        <button type="button" onClick={submit} disabled={busy} data-testid="finance-import-submit"
          className="h-10 px-5 inline-flex items-center gap-2 bg-[#6E1E3A] text-white text-sm font-semibold disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />} Import &amp; auto-assign
        </button>
      </div>
    </section>
  );
};